/**
 * Sector operations — Stays PMS connector client service.
 * R39 K12.a + K12.b (plan docs/plans/sector-operations.md SO-D201, SO-D207, SO-D212, SO-P201).
 */

import { doc, onSnapshot, type Unsubscribe } from 'firebase/firestore'
import { firestoreDb } from './firebase'
import { callable } from './callable'
import type {
  PmsAdapterType,
  StaysPmsConnectionMetadata,
  ReducedPmsEvent,
  OpsPmsOutboxItem,
  OpsPmsOutboxState,
  ImportStaysPmsReportInput,
  ImportStaysPmsReportResult,
} from '../types/ops'

export interface CreateStaysPmsConnectionInput {
  workspaceId: string
  adapter?: PmsAdapterType
  authCode?: string
  consumerKey?: string
  consumerSecret?: string
  hotelId?: string
  counterId?: string
  email?: string
  password?: string
  writeBack?: {
    enabled: boolean
    callbackUrl?: string
  }
  propertyMap?: Record<string, string>
  reportColumns?: Record<string, string>
  opId?: string
}

export interface CreateStaysPmsConnectionResult {
  connectionId: string
  plaintextSecret: string
  webhookUrl: string
}

export interface RotateStaysPmsSecretInput {
  workspaceId: string
  opId?: string
}

export interface RotateStaysPmsSecretResult {
  plaintextSecret: string
}

export interface UpdateStaysPmsConnectionInput {
  workspaceId: string
  adapter?: PmsAdapterType
  authCode?: string
  consumerKey?: string
  consumerSecret?: string
  hotelId?: string
  counterId?: string
  email?: string
  password?: string
  writeBack?: {
    enabled: boolean
    callbackUrl?: string
  }
  propertyMap?: Record<string, string>
  reportColumns?: Record<string, string>
  status?: 'ACTIVE' | 'DISABLED'
  enabled?: boolean
  opId?: string
}

export interface UpdateStaysPmsConnectionResult {
  updated: boolean
}

export interface DeleteStaysPmsConnectionInput {
  workspaceId: string
  opId?: string
}

export interface DeleteStaysPmsConnectionResult {
  deleted: boolean
}

export interface StaysPmsEventRecord extends ReducedPmsEvent {
  result: 'APPLIED' | 'REPLAYED' | 'UNMAPPED' | 'IGNORED' | 'INVALID'
  roomId?: string | null
  reason?: string | null
  processedAt?: unknown
}

export interface StaysPmsStatusResult {
  connection: StaysPmsConnectionMetadata | null
  events: StaysPmsEventRecord[]
  outboxCounts: Record<OpsPmsOutboxState, number>
  failedOutbox: OpsPmsOutboxItem[]
}

export interface RetryStaysPmsOutboxInput {
  workspaceId: string
  outboxIds?: string[]
}

export interface RetryStaysPmsOutboxResult {
  requeuedCount: number
}

export function subscribeToStaysPmsConnection(
  workspaceId: string,
  callback: (conn: StaysPmsConnectionMetadata | null) => void,
): Unsubscribe {
  if (!workspaceId) {
    callback(null)
    return () => {}
  }
  const ref = doc(firestoreDb, 'connection_metadata', `${workspaceId}:::STAYS_PMS`)
  return onSnapshot(
    ref,
    (snap) => {
      if (!snap.exists()) {
        callback(null)
      } else {
        callback(snap.data() as StaysPmsConnectionMetadata)
      }
    },
    () => {
      callback(null)
    },
  )
}

export async function createStaysPmsConnection(
  input: CreateStaysPmsConnectionInput,
): Promise<CreateStaysPmsConnectionResult> {
  return callable<CreateStaysPmsConnectionInput, CreateStaysPmsConnectionResult>(
    'createStaysPmsConnection',
    input,
  )
}

export async function rotateStaysPmsSecret(
  input: RotateStaysPmsSecretInput,
): Promise<RotateStaysPmsSecretResult> {
  return callable<RotateStaysPmsSecretInput, RotateStaysPmsSecretResult>(
    'rotateStaysPmsSecret',
    input,
  )
}

export async function updateStaysPmsConnection(
  input: UpdateStaysPmsConnectionInput,
): Promise<UpdateStaysPmsConnectionResult> {
  return callable<UpdateStaysPmsConnectionInput, UpdateStaysPmsConnectionResult>(
    'updateStaysPmsConnection',
    input,
  )
}

export async function deleteStaysPmsConnection(
  input: DeleteStaysPmsConnectionInput,
): Promise<DeleteStaysPmsConnectionResult> {
  return callable<DeleteStaysPmsConnectionInput, DeleteStaysPmsConnectionResult>(
    'deleteStaysPmsConnection',
    input,
  )
}

export async function getStaysPmsStatus(
  input: { workspaceId: string },
): Promise<StaysPmsStatusResult> {
  return callable<{ workspaceId: string }, StaysPmsStatusResult>(
    'getStaysPmsStatus',
    input,
  )
}

export async function retryStaysPmsOutbox(
  input: RetryStaysPmsOutboxInput,
): Promise<RetryStaysPmsOutboxResult> {
  return callable<RetryStaysPmsOutboxInput, RetryStaysPmsOutboxResult>(
    'retryStaysPmsOutbox',
    input,
  )
}

export interface TestStaysPmsEzeeHotelResult {
  hotelCode: string
  propertyId: string
  propertyName?: string
  totalRooms: number
  mappedRooms: number
  unmappedRoomNames: string[]
}

export interface TestStaysPmsEzeeConnectionInput {
  workspaceId: string
  hotelCode?: string
  authCode?: string
  propertyMap?: Record<string, string>
  opId?: string
}

export interface TestStaysPmsEzeeConnectionResult {
  hotels: TestStaysPmsEzeeHotelResult[]
  totalRooms: number
  mappedRooms: number
  unmappedRoomNames: string[]
}

export async function testStaysPmsEzeeConnection(
  input: TestStaysPmsEzeeConnectionInput,
): Promise<TestStaysPmsEzeeConnectionResult> {
  return callable<TestStaysPmsEzeeConnectionInput, TestStaysPmsEzeeConnectionResult>(
    'testStaysPmsEzeeConnection',
    input,
  )
}

export interface TestStaysPmsHotelogixConnectionInput {
  workspaceId: string
  consumerKey?: string
  consumerSecret?: string
  hotelId?: string
  counterId?: string
  email?: string
  password?: string
  propertyMap?: Record<string, string>
  opId?: string
}

export interface TestStaysPmsHotelogixConnectionResult {
  hotelCode: string
  propertyId: string
  propertyName?: string
  totalRooms: number
  mappedRooms: number
  unmappedRoomNames: string[]
}

export async function testStaysPmsHotelogixConnection(
  input: TestStaysPmsHotelogixConnectionInput,
): Promise<TestStaysPmsHotelogixConnectionResult> {
  return callable<TestStaysPmsHotelogixConnectionInput, TestStaysPmsHotelogixConnectionResult>(
    'testStaysPmsHotelogixConnection',
    input,
  )
}

export async function importStaysPmsReport(
  input: ImportStaysPmsReportInput,
): Promise<ImportStaysPmsReportResult> {
  return callable<ImportStaysPmsReportInput, ImportStaysPmsReportResult>(
    'importStaysPmsReport',
    input,
  )
}
